// Giotto Next salva i canvas con Fabric 7, senza convertirli: questo modulo li
// riporta al formato di Fabric 5.2.1 che usa questo editor. È la stessa
// trasformazione di `encodeCanvas` in giotto-next (app/src/giotto/canvas-codec.ts),
// con una differenza: qui non si interrompe, ma raccoglie cosa non si può
// rappresentare così da mostrarlo all'utente prima di convertire.

const LEGACY_VERSION = '5.2.1';

const legacyTypes = {
  Rect: 'rect',
  Circle: 'circle',
  Ellipse: 'ellipse',
  Line: 'line',
  Triangle: 'triangle',
  Polygon: 'polygon',
  Polyline: 'polyline',
  Path: 'path',
  Text: 'text',
  IText: 'i-text',
  Textbox: 'textbox',
  Image: 'image',
  Group: 'group',
  ActiveSelection: 'activeSelection',
  MagicFrame: 'MagicFrame',
  Eraser: 'eraser',
};

const supportedTypes = new Set([
  ...Object.keys(legacyTypes),
  ...Object.values(legacyTypes),
  'clipping',
]);

const nestedKeys = [
  'clipPath',
  'backgroundImage',
  'overlayImage',
  'clipping',
  'editingImgProps',
  'lastProps',
];

const isObject = (value) =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const clone = (value) => JSON.parse(JSON.stringify(value));

const unwrap = (raw) =>
  isObject(raw) && isObject(raw.canvas) && !Array.isArray(raw.objects)
    ? raw.canvas
    : raw;

const parse = (input) => {
  if (typeof input !== 'string') return isObject(input) ? input : null;
  if (!input) return null;
  try {
    return JSON.parse(input);
  } catch (error) {
    return null;
  }
};

export const getCanvasJsonMajorVersion = (input) => {
  const canvas = unwrap(parse(input));
  if (!isObject(canvas) || typeof canvas.version !== 'string') return null;
  const major = Number.parseInt(canvas.version, 10);
  return Number.isFinite(major) ? major : null;
};

// Fabric 6 ha cambiato il formato del JSON: da lì in su il canvas viene da Next.
export const isNextCanvasJson = (input) =>
  (getCanvasJsonMajorVersion(input) ?? 0) >= 6;

export const hasNextCanvasPages = (pages = []) =>
  pages.some((page) => page && isNextCanvasJson(page.canvasJson));

// Fabric 7 salva gli stili del testo come intervalli di caratteri,
// Fabric 5 come mappa riga → colonna.
const stylesToLegacy = (styles, text) => {
  const result = {};
  let index = 0;
  text.split(/\r?\n/).forEach((line, lineIndex) => {
    // graphemeSplit di Fabric conta i code point Unicode, a capo esclusi.
    Array.from(line).forEach((_, column) => {
      const range = styles.find(
        (item) =>
          isObject(item) &&
          Number(item.start) <= index &&
          index < Number(item.end)
      );
      if (isObject(range) && isObject(range.style)) {
        result[lineIndex] = result[lineIndex] || {};
        result[lineIndex][column] = clone(range.style);
      }
      index++;
    });
  });
  return result;
};

const toLegacy = (value, losses) => {
  const result = clone(value);

  if (result.type) {
    const centered = ['MagicFrame', 'eraser', 'clipping'].includes(
      String(result.type)
    );
    if (result.originX === undefined || result.originX === null) {
      result.originX = centered ? 'center' : 'left';
    }
    if (result.originY === undefined || result.originY === null) {
      result.originY = centered ? 'center' : 'top';
    }
    result.type = legacyTypes[result.type] || result.type;
  }

  if (Array.isArray(result.objects)) {
    result.objects = result.objects
      .filter((child) => {
        if (!isObject(child)) return false;
        if (typeof child.type === 'string' && !supportedTypes.has(child.type)) {
          losses.push(`Oggetto di tipo "${child.type}" rimosso`);
          return false;
        }
        return true;
      })
      .map((child) => toLegacy(child, losses));
  }

  nestedKeys.forEach((key) => {
    if (isObject(result[key])) result[key] = toLegacy(result[key], losses);
  });

  delete result.layoutManager;

  if (Array.isArray(result.styles)) {
    result.styles = stylesToLegacy(result.styles, String(result.text ?? ''));
  }

  // In Fabric 7 gomma e ritaglio stanno insieme in un unico clipPath;
  // Fabric 5 li tiene separati in `eraser` e `clipPath`.
  const clipping = result.clipPath;
  if (isObject(clipping) && clipping.type === 'clipping') {
    const children = Array.isArray(clipping.objects)
      ? clipping.objects.filter(isObject)
      : [];
    const masks = children.filter((child) => child.type !== 'path');
    if (masks.length > 1) {
      losses.push(
        'Maschera composta semplificata: resta solo la prima forma di ritaglio'
      );
    }
    result.eraser = {
      ...clipping,
      type: 'eraser',
      objects: children.filter((child) => child.type === 'path'),
    };
    delete result.eraser.giottoOriginalClipPath;
    delete result.clipPath;
    if (masks[0]) {
      const mask = masks[0];
      result.clipPath =
        mask.giottoOriginalClipPath && Array.isArray(mask.objects)
          ? mask.objects[0]
          : mask;
    }
  }

  return result;
};

/**
 * Converte il canvasJson di una pagina di Giotto Next nel formato Fabric 5.
 * I canvas già legacy (o vuoti) tornano invariati.
 *
 * @returns {{ canvasJson: string, converted: boolean, losses: string[] }}
 */
export const convertNextCanvasJson = (input) => {
  if (!isNextCanvasJson(input)) {
    return { canvasJson: input, converted: false, losses: [] };
  }
  const losses = [];
  const canvas = unwrap(parse(input));
  const legacy = toLegacy(
    { ...canvas, objects: Array.isArray(canvas.objects) ? canvas.objects : [] },
    losses
  );
  return {
    canvasJson: JSON.stringify({ ...legacy, version: LEGACY_VERSION }),
    converted: true,
    losses,
  };
};

/**
 * Converte tutte le pagine di Next di un elenco. Le perdite sono raccolte per
 * pagina (numerata da 1) e senza ripetizioni.
 */
export const convertNextPages = (pages = []) => {
  const losses = [];
  const converted = pages.map((page, index) => {
    if (!page || !isNextCanvasJson(page.canvasJson)) return page;
    const result = convertNextCanvasJson(page.canvasJson);
    [...new Set(result.losses)].forEach((loss) =>
      losses.push(`Pagina ${index + 1}: ${loss}`)
    );
    return { ...page, canvasJson: result.canvasJson };
  });
  return { pages: converted, losses };
};
